import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EventMarketDetail } from '../api/types';

function parseMarketIds(param: string | null): Set<string> {
    if (!param) return new Set();
    return new Set(param.split(',').filter(Boolean));
}

function serializeMarketIds(ids: Set<string>): string {
    return [...ids].join(',');
}

interface UseMarketSelectionParams {
    markets: EventMarketDetail[];
    isSingleMarket: boolean;
}

export function useMarketSelection({ markets, isSingleMarket }: UseMarketSelectionParams) {
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedMarketIds = useMemo(
        () => parseMarketIds(searchParams.get('markets') ?? searchParams.get('market')),
        [searchParams]
    );

    const autoExpandId = isSingleMarket ? markets[0]?.condition_id : null;

    const effectiveSelectedIds = useMemo(() => {
        if (isSingleMarket && autoExpandId) return new Set([autoExpandId]);
        if (selectedMarketIds.size > 0) {
            return new Set(
                [...selectedMarketIds].filter(id => markets.some(m => m.condition_id === id))
            );
        }
        return selectedMarketIds;
    }, [isSingleMarket, autoExpandId, selectedMarketIds, markets]);

    // True only when user has explicitly selected markets in a multi-market event
    const hasSelection = effectiveSelectedIds.size > 0 && !isSingleMarket;

    const updateSelectedMarkets = useCallback(
        (newIds: Set<string>) => {
            const params = new URLSearchParams(searchParams);
            params.delete('market');
            if (newIds.size > 0) {
                params.set('markets', serializeMarketIds(newIds));
            } else {
                params.delete('markets');
            }
            setSearchParams(params, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const handleToggleSelect = useCallback(
        (conditionId: string) => {
            if (isSingleMarket) return;
            const newIds = new Set(effectiveSelectedIds);
            if (newIds.has(conditionId)) {
                newIds.delete(conditionId);
            } else {
                newIds.add(conditionId);
            }
            updateSelectedMarkets(newIds);
        },
        [effectiveSelectedIds, isSingleMarket, updateSelectedMarkets]
    );

    return {
        selectedMarketIds,
        effectiveSelectedIds,
        hasSelection,
        updateSelectedMarkets,
        handleToggleSelect,
    };
}